import React, { useState, useEffect, useContext, useMemo, useCallback, useRef } from "react";
import { Navigate } from "react-router-dom";
import "../../../../App.css";
import Navbar from "../../../ui/Navbar";
import Loader from "../../../ui/Loader";
import Table from "../../../ui/Table";
import { AuthContext } from "../../../../context/AuthContext";
import { swalError, swalSuccess } from "../../../../utils/swalAlert";
import { fetchWithAuth } from "../../../../utils/api";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const serverUrl = process.env.REACT_APP_SERVER_URL || "";

export default function LibraryStudentStats() {
  const [showDatePopup, setShowDatePopup] = useState(false);
  const [rangeOption, setRangeOption] = useState("Today");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [statsData, setStatsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter state for the table
  const [selectedStdTest, setSelectedStdTest] = useState("ALL");

  const { isAuthenticated, primaryRole } = useContext(AuthContext);
  const abortControllerRef = useRef(null);

  const breadcrumbs = [
    { name: "Home", path: "/superadmin", isLast: false },
    { name: "Library Stats", path: "/superadmin/library-stats", isLast: true },
  ];

  document.title = "GradeMe-AI: Library Student Stats";

  const formatPhoneNumber = (phone) => {
    if (!phone) return "-";
    const str = String(phone).trim();
    const cleaned = str.replace(/\D/g, "");
    if (!cleaned) return str;

    if (cleaned.length === 12 && cleaned.startsWith("91")) {
      return `+91 ${cleaned.slice(2, 7)} ${cleaned.slice(7)}`;
    }
    if (cleaned.length === 10) {
      return `+91 ${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
    }
    if (cleaned.length === 11 && cleaned.startsWith("0")) {
      return `+91 ${cleaned.slice(1, 6)} ${cleaned.slice(6)}`;
    }
    if (str.startsWith("+")) {
      return str;
    }
    return `+${cleaned}`;
  };

  const userDate = useCallback((dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toISOString().slice(0, 10);
  }, []);

  const displayDateOnly = useCallback((dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString(navigator.language, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, []);

  const getTimeRangeText = useCallback(() => {
    if (rangeOption === "Custom") {
      return startDate && endDate
        ? `${displayDateOnly(startDate)} - ${displayDateOnly(endDate)}`
        : "Select Date Range";
    }
    return rangeOption;
  }, [rangeOption, startDate, endDate, displayDateOnly]);

  const setTodayRange = useCallback(() => {
    const today = new Date();
    const startOfDay = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    setStartDate(userDate(startOfDay));
    setEndDate(userDate(today));
    setRangeOption("Today");
  }, [userDate]);

  useEffect(() => {
    setTodayRange();
  }, [setTodayRange]);

  const handlePresetSelect = (option) => {
    setRangeOption(option);
    const now = new Date();
    let start, end;

    const formatDate = (date) => {
      const offset = date.getTimezoneOffset();
      const localDate = new Date(date.getTime() - offset * 60000);
      return localDate.toISOString().slice(0, 10);
    };

    switch (option) {
      case "Today":
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        end = now;
        break;
      case "Last 24 Hours":
        start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        end = now;
        break;
      case "This Week":
        {
          const dayOfWeek = now.getDay();
          const distanceToMon = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
          start = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate() - distanceToMon
          );
          end = now;
        }
        break;
      case "This Month":
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = now;
        break;
      case "All Time":
        setStartDate("");
        setEndDate("");
        return;
      case "Custom":
      default:
        return;
    }

    setStartDate(formatDate(start));
    setEndDate(formatDate(end));
  };

  const fetchStats = useCallback(async (signal) => {
    setLoading(true);
    try {
      let filterParam = "today";
      if (rangeOption === "Today") filterParam = "today";
      else if (rangeOption === "Last 24 Hours") filterParam = "last_24_hours";
      else if (rangeOption === "This Week") filterParam = "this_week";
      else if (rangeOption === "This Month") filterParam = "this_month";
      else if (rangeOption === "All Time") filterParam = "all";
      else if (rangeOption === "Custom") filterParam = "custom";

      const params = new URLSearchParams({ filterType: filterParam });

      if (startDate) {
        params.append("startDate", startDate);
      }
      if (endDate) {
        params.append("endDate", endDate);
      }

      const response = await fetchWithAuth(
        `${serverUrl}/api/library-mcq-bank/get-student-library-stats?${params.toString()}`,
        { credentials: "include", signal }
      );
      if (!response) return;
      if (!response.ok) throw new Error("Failed to fetch library student stats");
      const result = await response.json();

      setStatsData(result);
      setError(null);
    } catch (err) {
      if (err.name === "AbortError") return;
      setError(err.message);
      swalError(err.message || "Failed to load library student stats.");
    } finally {
      setLoading(false);
    }
  }, [rangeOption, startDate, endDate]);

  // Export filtered table data to Excel (.xlsx)
  const exportToExcel = async () => {
    if (!filteredStudentStats || filteredStudentStats.length === 0) {
      swalError("No student library activity data available to export.");
      return;
    }

    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Student Library Stats");

      // Columns requested: Student Name, Student ID, Email, Phone Number, Standardized Test Name, Tests Submitted
      worksheet.columns = [
        { header: "Student Name", key: "studentName", width: 25 },
        { header: "Student ID", key: "referenceId", width: 20 },
        { header: "Email", key: "email", width: 32 },
        { header: "Phone Number", key: "phoneNumber", width: 20 },
        { header: "Standardized Test Name", key: "templateName", width: 28 },
        { header: "Tests Submitted", key: "testsSubmittedCount", width: 18 },
      ];

      const headerRow = worksheet.getRow(1);
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFF" }, size: 11 };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "4F46E5" },
        };
        cell.alignment = { vertical: "middle", horizontal: "center" };
      });
      headerRow.height = 26;

      filteredStudentStats.forEach((item) => {
        const rawPhone = item.phoneNumber || item.phone || item.mobileNumber;
        const phoneVal = formatPhoneNumber(rawPhone);
        const row = worksheet.addRow({
          studentName: item.studentName || "-",
          referenceId: item.referenceId || item.studentReferenceId || "-",
          email: item.email || "-",
          phoneNumber: phoneVal,
          templateName: item.templateName || "-",
          testsSubmittedCount: item.testsSubmittedCount || 0,
        });

        row.getCell("testsSubmittedCount").alignment = { horizontal: "center" };

        const phoneCell = row.getCell("phoneNumber");
        if (phoneVal !== "-") {
          phoneCell.value = String(phoneVal);
          phoneCell.numFmt = "@";
        }
      });

      worksheet.eachRow((row) => {
        row.eachCell((cell) => {
          cell.border = {
            top: { style: "thin", color: { argb: "CBD5E1" } },
            left: { style: "thin", color: { argb: "CBD5E1" } },
            bottom: { style: "thin", color: { argb: "CBD5E1" } },
            right: { style: "thin", color: { argb: "CBD5E1" } },
          };
        });
      });

      const now = new Date();
      const dateStr = `${String(now.getDate()).padStart(2, "0")}-${String(now.getMonth() + 1).padStart(2, "0")}-${now.getFullYear()}`;
      const filename = `Student_Library_Stats_${dateStr}.xlsx`;

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, filename);
      swalSuccess("Excel exported successfully!");
    } catch (err) {
      console.error("Failed to export Excel:", err);
      swalError("Failed to export Excel file.");
    }
  };

  useEffect(() => {
    if (!rangeOption) return;

    if (
      rangeOption === "All Time" ||
      (rangeOption !== "Custom" && startDate && endDate) ||
      (rangeOption === "Custom" && startDate && endDate)
    ) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;

      if (rangeOption === "Custom") {
        const timer = setTimeout(() => {
          fetchStats(controller.signal);
        }, 500);
        return () => {
          clearTimeout(timer);
          controller.abort();
        };
      } else {
        fetchStats(controller.signal);
        return () => {
          controller.abort();
        };
      }
    }
  }, [fetchStats, rangeOption, startDate, endDate]);

  // Extract unique standardized test options for filter dropdown
  const stdTestOptions = useMemo(() => {
    if (!statsData || !statsData.standardizedTestBreakdown) return [];
    return statsData.standardizedTestBreakdown.map((item) => ({
      templateId: item.templateId,
      templateName: item.templateName,
    }));
  }, [statsData]);

  // Table Columns Definition
  const tableColumns = useMemo(
    () => [
      {
        key: "studentName",
        label: "Student Name",
        sortable: true,
        render: (row) => (
          <div>
            <div style={{ fontWeight: 600, color: "#1e293b" }}>{row.studentName}</div>
            <div style={{ fontSize: "0.78rem", color: "#64748b", fontWeight: 400 }}>
              {row.email}
            </div>
          </div>
        ),
      },
      {
        key: "referenceId",
        label: "Student ID",
        sortable: true,
        sortBy: (row) => row.referenceId || "",
        render: (row) => (
          <span style={{ fontSize: "0.85rem", color: "#334155", fontWeight: 500, whiteSpace: "nowrap" }}>
            {row.referenceId || "-"}
          </span>
        ),
      },
      {
        key: "phoneNumber",
        label: "Phone Number",
        sortable: true,
        sortBy: (row) => formatPhoneNumber(row.phoneNumber || row.phone || row.mobileNumber),
        render: (row) => (
          <span style={{ fontSize: "0.85rem", color: "#334155", fontWeight: 500, whiteSpace: "nowrap" }}>
            {formatPhoneNumber(row.phoneNumber || row.phone || row.mobileNumber)}
          </span>
        ),
      },
      {
        key: "templateName",
        label: "Standardized Test",
        sortable: true,
        render: (row) => (
          <span
            style={{
              backgroundColor: "#eef2ff",
              color: "#4f46e5",
              padding: "4px 10px",
              borderRadius: "12px",
              fontWeight: 600,
              fontSize: "0.8rem",
            }}
          >
            {row.templateName}
          </span>
        ),
      },
      {
        key: "testsSubmittedCount",
        label: "Tests Submitted",
        sortable: true,
        render: (row) => (
          <span
            style={{
              backgroundColor: "#f0fdf4",
              color: "#166534",
              padding: "4px 12px",
              borderRadius: "12px",
              fontWeight: 700,
              fontSize: "0.85rem",
            }}
          >
            {row.testsSubmittedCount}
          </span>
        ),
      },
      {
        key: "lastSubmittedAt",
        label: "Last Submission",
        sortable: true,
        render: (row) => formatDate(row.lastSubmittedAt),
      },
    ],
    []
  );

  // Filtered Student Stats list based on standardized test dropdown
  const filteredStudentStats = useMemo(() => {
    if (!statsData || !statsData.studentStats) return [];
    return statsData.studentStats
      .map((row, idx) => ({
        ...row,
        _id: row._id || `${row.studentId}_${row.templateId}_${idx}`,
      }))
      .filter((row) => {
        if (selectedStdTest !== "ALL" && row.templateId !== selectedStdTest) {
          return false;
        }
        return true;
      });
  }, [statsData, selectedStdTest]);

  if (!isAuthenticated || primaryRole !== "superadmin") {
    return <Navigate to="/login" replace />;
  }


  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <>
      <Navbar pageName="Library Stats & Student Usage Analytics" breadcrumbs={breadcrumbs} />
      <div className="manage-token">
        {/* Standardized Test Filter & Date Range Selection Bar */}
        <div
          className="controls-section"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: "16px",
            position: "relative",
            zIndex: showDatePopup ? 1000 : 10,
          }}
        >
          <div className="type-selector" style={{ flex: "1", minWidth: "260px" }}>
            <label>Standardized Test:</label>
            <div className="type-buttons">
              <button
                type="button"
                className={`type-btn ${selectedStdTest === "ALL" ? "active" : ""}`}
                onClick={() => setSelectedStdTest("ALL")}
              >
                <span className="type-icon">🎓</span>
                All
              </button>
              {stdTestOptions.map((opt) => (
                <button
                  key={opt.templateId}
                  type="button"
                  className={`type-btn ${selectedStdTest === opt.templateId ? "active" : ""}`}
                  onClick={() => setSelectedStdTest(opt.templateId)}
                >
                  <span className="type-icon">📝</span>
                  {opt.templateName}
                </button>
              ))}
            </div>
          </div>

          {/* Date Range Selector (Right side of Card) */}
          <div className="time-range-wrapper" style={{ marginTop: "4px" }}>
            <button
              type="button"
              onClick={() => setShowDatePopup((prev) => !prev)}
              className="time-range-btn"
              style={{ marginBottom: 0 }}
              aria-expanded={showDatePopup}
              aria-haspopup="true"
            >
              <span className="btn-icon">🕒</span>
              <span className="time-range-text">{getTimeRangeText()}</span>
              <span className={`chevron ${showDatePopup ? "open" : ""}`}>▼</span>
            </button>

            {showDatePopup && (
              <>
                <div
                  className="popup-overlay"
                  onClick={() => setShowDatePopup(false)}
                />
                <div className="time-range-popup" style={{ right: 0, left: "auto" }}>
                  <div className="popup-header">
                    <h3>Select Time Range</h3>
                    <button
                      type="button"
                      className="close-btn"
                      onClick={() => setShowDatePopup(false)}
                      aria-label="Close"
                    >
                      ✕
                    </button>
                  </div>

                  <div className="preset-section">
                    <div className="preset-grid">
                      {[
                        "Today",
                        "Last 24 Hours",
                        "This Week",
                        "This Month",
                        "Custom",
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`preset-option ${
                            rangeOption === option ? "selected" : ""
                          }`}
                          onClick={() => handlePresetSelect(option)}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="custom-range-section">
                    <div className="date-inputs">
                      <div className="input-group">
                        <label htmlFor="start-date">Start Date</label>
                        <input
                          id="start-date"
                          type="date"
                          value={startDate}
                          onChange={(e) => {
                            setStartDate(e.target.value);
                            setRangeOption("Custom");
                          }}
                          className="date-input"
                        />
                      </div>
                      <div className="input-group">
                        <label htmlFor="end-date">End Date</label>
                        <input
                          id="end-date"
                          type="date"
                          value={endDate}
                          onChange={(e) => {
                            setEndDate(e.target.value);
                            setRangeOption("Custom");
                          }}
                          className="date-input"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {loading ? (
          <div
            style={{
              padding: "100px 0",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Loader style={{ position: "static", left: "auto", top: "auto", fontSize: "2.5rem", color: "#000000" }} />
          </div>
        ) : error ? (
          <div className="error-div" style={{ margin: "20px 0" }}>{error}</div>
        ) : (
          <>
            {/* Student Activity Data Table Section */}
            <div className="table-section" style={{ position: "relative" }}>
              <div className="table-header">
                <button
                  className="export-csv-btn"
                  onClick={exportToExcel}
                  style={{ whiteSpace: "nowrap" }}
                >
                  <span className="export-icon">📊</span>
                  <span className="export-text">Export Usage</span>
                </button>
              </div>
              <Table
                data={filteredStudentStats}
                columns={tableColumns}
                caption="Student Library Activity Details"
                pagination={true}
                pageSize={25}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
}
